import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp, LANGS, LANG_LABELS } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { initials, avatarColor } from "@/lib/utils2";
import { PushSettings } from "@/components/PushControls";

export default function More() {
  const { t, lang, setLang, theme, toggleTheme } = useApp();
  const { user, setUser, logout, isRole } = useAuth();
  const navigate = useNavigate();
  const [editProfile, setEditProfile] = useState(false);

  const items = [
    ...(isRole("president", "tresorier") ? [{ icon: "UsersRound", label: t("user_management"), to: "/users", testid: "more-users" }] : []),
    { icon: "Building2", label: t("building"), to: "/lots", testid: "more-lots" },
    { icon: "Contact", label: t("pro_directory"), to: "/professionals", testid: "more-pros" },
    { icon: "Users", label: t("residents"), to: "/residents", testid: "more-residents" },
    { icon: "FolderOpen", label: t("documents"), to: "/documents", testid: "more-documents" },
    { icon: "Gavel", label: t("assemblies"), to: "/assemblies", testid: "more-assemblies" },
    { icon: "ClipboardList", label: t("maintenance"), to: "/maintenance", testid: "more-maintenance" },
    { icon: "Scale", label: t("legal"), to: "/legal", testid: "more-legal" },
    { icon: "Wrench", label: t("tickets"), to: "/tickets", testid: "more-tickets" },
    { icon: "KeyRound", label: t("change_pin"), to: "/change-pin", testid: "more-change-pin" },
    ...(isRole("president") ? [{ icon: "DatabaseBackup", label: t("bkp_title"), to: "/backup", testid: "more-backup" }] : []),
    ...(isRole("president", "tresorier", "conseil") ? [{ icon: "ShieldCheck", label: t("audit_log"), to: "/audit", testid: "more-audit" }] : []),
  ];

  return (
    <div className="space-y-5" data-testid="more-page">
      {/* Profile */}
      <div className="rounded-2xl bg-card border border-border p-5 flex items-center gap-4 shadow-ios">
        <div className="h-16 w-16 rounded-full flex items-center justify-center text-white text-xl font-bold shrink-0" style={{ backgroundColor: avatarColor(user?.name) }}>
          {initials(user?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-lg truncate">{user?.name}</p>
          <p className="text-sm text-muted-foreground truncate">@{user?.identifier}</p>
          <span className="inline-block mt-1 text-xs font-semibold bg-brand/10 text-brand dark:text-primary px-2 py-0.5 rounded-full">{t("role_" + user?.role)}</span>
        </div>
        <button onClick={() => setEditProfile(true)} data-testid="edit-profile-btn"
          className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0 active:scale-95 transition-transform"><Icons.Pencil className="h-4 w-4" /></button>
      </div>

      {editProfile && <ProfileModal user={user} onClose={() => setEditProfile(false)} onSaved={(u) => { setUser(u); setEditProfile(false); }} t={t} />}

      {/* Menu */}
      <div className="rounded-2xl bg-card border border-border divide-y divide-border overflow-hidden shadow-ios">
        {items.map((it) => {
          const Ico = Icons[it.icon] || Icons.Circle;
          return (
            <button key={it.to} onClick={() => navigate(it.to)} data-testid={it.testid}
              className="w-full p-4 flex items-center gap-3 active:bg-accent hover:bg-accent/60 transition-colors text-start">
              <span className="h-9 w-9 rounded-xl bg-brand/10 text-brand dark:text-primary flex items-center justify-center shrink-0"><Ico className="h-5 w-5" /></span>
              <span className="font-semibold flex-1">{it.label}</span>
              <Icons.ChevronRight className="h-5 w-5 text-muted-foreground rtl:rotate-180" />
            </button>
          );
        })}
      </div>

      {/* Settings */}
      <div className="rounded-2xl bg-card border border-border p-4 space-y-4 shadow-ios">
        <p className="font-bold">{t("settings")}</p>
        <div>
          <span className="font-semibold flex items-center gap-2 mb-2"><Icons.Languages className="h-5 w-5" /> {t("language")}</span>
          <div className="grid grid-cols-3 gap-2">
            {LANGS.map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                className={`h-11 rounded-xl text-sm font-semibold transition-colors ${lang === l ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
                {LANG_LABELS[l]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-semibold flex items-center gap-2">{theme === "light" ? <Icons.Moon className="h-5 w-5" /> : <Icons.Sun className="h-5 w-5" />} {t("dark_mode")}</span>
          <button onClick={toggleTheme} data-testid="more-theme-toggle"
            className={`h-8 w-14 rounded-full p-1 transition-colors ${theme === "dark" ? "bg-brand" : "bg-secondary"}`}>
            <span className={`block h-6 w-6 rounded-full bg-white transition-transform ${theme === "dark" ? "translate-x-6 rtl:-translate-x-6" : ""}`} />
          </button>
        </div>
      </div>

      <PushSettings t={t} />

      {isRole("president", "tresorier") && <EmailSettings t={t} />}

      <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"
        className="w-full h-12 rounded-xl bg-destructive/10 text-destructive font-bold flex items-center justify-center gap-2">
        <Icons.LogOut className="h-5 w-5" /> {t("logout")}
      </button>
    </div>
  );
}


// Carte « E-mails » (président/trésorier) : statut d'envoi + e-mail de test.
function EmailSettings({ t }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false }));
  }, []);

  const sendTest = async () => {
    setBusy(true);
    try { await api.post("/email/test"); toast.success(t("email_test_ok")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const enabled = status?.enabled;
  return (
    <div className="rounded-2xl bg-card border border-border p-4 space-y-3 shadow-ios" data-testid="email-settings">
      <p className="font-bold flex items-center gap-2"><Icons.Mail className="h-5 w-5" /> {t("email_section")}</p>
      <div className="flex items-center gap-2 text-sm">
        <span className={`h-2.5 w-2.5 rounded-full ${enabled ? "bg-emerald-500" : "bg-amber-500"}`} />
        <span className="text-muted-foreground">{enabled ? t("email_configured") : t("email_not_configured")}</span>
      </div>
      <button onClick={sendTest} disabled={busy || !enabled} data-testid="email-test-btn"
        className="w-full h-11 rounded-xl bg-secondary text-secondary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        <Icons.Send className="h-4 w-4" /> {t("email_test_btn")}
      </button>
    </div>
  );
}


// Modale d'édition de son propre profil (se répercute dans l'annuaire des résidents).
function ProfileModal({ user, onClose, onSaved, t }) {
  const [form, setForm] = useState({
    name: user?.name || "", apartment: user?.apartment || "",
    email: user?.email || "", phone: user?.phone || "",
    occupancy: user?.occupancy || "", tenant_name: user?.tenant_name || "", tenant_phone: user?.tenant_phone || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/me/profile", form);
      toast.success(t("profile_updated"));
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("login_error")); }
    finally { setBusy(false); }
  };

  const field = "w-full h-12 rounded-xl border border-input bg-background px-4 outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-md p-6 shadow-ios-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{t("edit_profile")}</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center"><Icons.X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("full_name")} data-testid="pm-name" className={field} />
          <input value={form.apartment} onChange={(e) => setForm({ ...form, apartment: e.target.value })} placeholder={t("apartment_label")} data-testid="pm-apartment" className={field} />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("email")} data-testid="pm-email" className={field} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone_ph")} data-testid="pm-phone" className={field} />

          {/* Occupation du logement — modifiable à tout moment */}
          <div className="pt-1">
            <p className="text-sm font-semibold text-muted-foreground mb-1.5">{t("occ_label")}</p>
            <div className="grid grid-cols-2 gap-2">
              {[["occupant", t("occ_occupant"), "Home"], ["loue", t("occ_loue"), "KeyRound"]].map(([val, lbl, ic]) => {
                const Ico = Icons[ic];
                const active = form.occupancy === val;
                return (
                  <button key={val} type="button" data-testid={`pm-occ-${val}`}
                    onClick={() => setForm({ ...form, occupancy: active ? "" : val })}
                    className={`h-12 rounded-xl border font-semibold text-sm flex items-center justify-center gap-1.5 ${active ? "bg-brand text-white border-brand" : "bg-background border-input"}`}>
                    <Ico className="h-4 w-4" /> {lbl}
                  </button>
                );
              })}
            </div>
          </div>
          {form.occupancy === "loue" && (
            <div className="space-y-3 rounded-xl bg-majorelle/5 border border-majorelle/20 p-3" data-testid="pm-tenant-fields">
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <Icons.Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-majorelle" /> {t("occ_why")}
              </p>
              <input value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
                placeholder={t("occ_tenant_name")} data-testid="pm-tenant-name" className={field} />
              <input value={form.tenant_phone} onChange={(e) => setForm({ ...form, tenant_phone: e.target.value })}
                placeholder={t("occ_tenant_phone")} data-testid="pm-tenant-phone" className={field} />
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t("directory_notice")}</p>
          <button onClick={save} disabled={busy} data-testid="pm-submit" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{busy ? t("loading") : t("save")}</button>
        </div>
      </div>
    </div>
  );
}
